// src/api/features/tour-types/queries.ts

import { useQuery, type QueryClient } from "@tanstack/react-query";

import { listTourTypes, getTourType } from "./api";

import type {
  TourType,
  TourTypeListQuery,
  PaginatedTourTypeItems,
} from "./types";

/* ======================================================
 * Query Keys
 * ====================================================== */

export const TourTypesKeys = {
  all: ["tour-types"] as const,
  list: (query: TourTypeListQuery = {}) =>
    ["tour-types", "list", query] as const,
  detail: (id: number | string) =>
    ["tour-types", "detail", String(id)] as const,
};

/* ======================================================
 * LIST hook – GET /tour-types
 * Returns: PaginatedTourTypeItems (unwrapped)
 * ====================================================== */

export function useTourTypesList(query: TourTypeListQuery = {}) {
  return useQuery<PaginatedTourTypeItems, Error>({
    queryKey: TourTypesKeys.list(query),
    queryFn: () => listTourTypes(query),

    // v5 pattern: keep previous page data while refetching
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/* ======================================================
 * DETAIL hook – GET /tour-types/:id
 * Returns: TourType
 * ====================================================== */

export function useTourTypeDetail(id: number | null) {
  const enabled = id != null;

  return useQuery<TourType, Error>({
    queryKey: enabled
      ? TourTypesKeys.detail(id as number)
      : ["tour-types", "detail", "disabled"],
    queryFn: () => {
      if (id == null) {
        // never runs due to enabled:false, just for TS
        return Promise.reject(new Error("Tour type id is required"));
      }
      return getTourType(id);
    },
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* ======================================================
 * Prefetch helpers (optional)
 * ====================================================== */

export function prefetchTourTypes(
  qc: QueryClient,
  query: TourTypeListQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: TourTypesKeys.list(query),
    queryFn: () => listTourTypes(query),
    staleTime: 30_000,
  });
}

export function prefetchTourType(qc: QueryClient, id: number) {
  return qc.prefetchQuery({
    queryKey: TourTypesKeys.detail(id),
    queryFn: () => getTourType(id),
    staleTime: 60_000,
  });
}
